import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { ShieldCheck, Copy, Download, AlertCircle, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const MfaSetup = () => {
  const { setupToken, token, user, enrolStart, enrolVerify } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState("loading"); // loading | scan | codes
  const [secret, setSecret] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [enrolledUser, setEnrolledUser] = useState(null);

  const init = useCallback(async () => {
    try {
      const data = await enrolStart();
      setSecret(data.secret);
      setOtpauthUri(data.otpauth_uri);
      setStage("scan");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "MFA already enabled") {
        toast.info("MFA is already enabled.");
        navigate("/login");
      } else {
        setError("Could not start MFA setup. Please sign in again.");
        setTimeout(() => navigate("/login"), 1500);
      }
    }
  }, [enrolStart, navigate]);

  useEffect(() => {
    if (!setupToken && !token) {
      navigate("/login");
      return;
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await enrolVerify(code.trim());
      setBackupCodes(data.backup_codes);
      setEnrolledUser(data.user);
      setStage("codes");
      toast.success("Two-factor authentication enabled!");
    } catch (err) {
      const message = err.response?.data?.detail || "Invalid code. Try again.";
      setError(typeof message === "string" ? message : "Invalid code. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [`CPC Pay — Backup Codes\nKeep these somewhere safe. Each code works once.\n\n${backupCodes.join("\n")}\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cpd-portal-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const finish = () => {
    const role = enrolledUser?.role || user?.role;
    navigate(role === "account_manager" ? "/admin/portal" : role === "vendor" ? "/vendor" : "/employee");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 bg-emerald-900 rounded-lg flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-emerald-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            CPC Pay Security
          </span>
        </div>

        <Card className="border-stone-200 shadow-lg" data-testid="mfa-setup-card">
          {stage === "loading" && (
            <CardContent className="py-16 flex flex-col items-center gap-3 text-stone-600">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-700" />
              Preparing your secure setup…
            </CardContent>
          )}

          {stage === "scan" && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Set up two-factor authentication
                </CardTitle>
                <CardDescription>
                  Scan the QR code with Google Authenticator, Authy, 1Password, or Microsoft Authenticator.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="mfa-setup-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}
                <div className="flex justify-center">
                  <div className="p-4 bg-white border border-stone-200 rounded-xl" data-testid="mfa-qr">
                    {otpauthUri && <QRCodeSVG value={otpauthUri} size={180} fgColor="#0C1F3A" />}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone-500">Or enter this key manually</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-stone-100 rounded-md px-3 py-2 break-all" data-testid="mfa-secret">
                      {secret}
                    </code>
                    <Button type="button" variant="outline" size="icon"
                      onClick={() => { navigator.clipboard.writeText(secret); toast.success("Key copied"); }}
                      data-testid="mfa-copy-secret-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <form onSubmit={handleVerify} className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="totp-code">Enter the 6-digit code from your app</Label>
                    <Input
                      id="totp-code"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      className="text-center text-lg tracking-[0.4em]"
                      required
                      data-testid="mfa-setup-code-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-full h-11"
                    disabled={submitting || code.length < 6} data-testid="mfa-setup-verify-btn">
                    {submitting ? "Verifying…" : "Verify & Enable"}
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {stage === "codes" && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
                  <KeyRound className="w-6 h-6 text-emerald-700" /> Save your backup codes
                </CardTitle>
                <CardDescription>
                  Each code works once. Use them if you lose access to your authenticator app. They won't be shown again.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-2 gap-2 bg-stone-100 rounded-xl p-4" data-testid="mfa-backup-codes">
                  {backupCodes.map((c) => (
                    <code key={c} className="text-center font-mono text-sm py-1.5 bg-white rounded-md border border-stone-200">
                      {c}
                    </code>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={downloadCodes} data-testid="mfa-download-codes-btn">
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                  <Button type="button" variant="outline" className="flex-1"
                    onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Codes copied"); }}
                    data-testid="mfa-copy-codes-btn">
                    <Copy className="w-4 h-4 mr-2" /> Copy all
                  </Button>
                </div>
                <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
                  <Checkbox checked={savedConfirmed} onCheckedChange={setSavedConfirmed} data-testid="mfa-confirm-saved-checkbox" />
                  I've saved my backup codes somewhere safe.
                </label>
                <Button type="button" className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-full h-11"
                  disabled={!savedConfirmed} onClick={finish} data-testid="mfa-setup-done-btn">
                  Continue to dashboard
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MfaSetup;
